import { Link, useNavigate } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Settings,
  LogOut,
  Wrench,
  Home,
  Shield,
  Calendar,
  Building2,
  QrCode,
  ScanLine,
  MessageSquare,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { authClient } from "@/lib/auth";
import { useCommunity } from "@/contexts/CommunityContext";
import { NotificationsBell } from "@/components/NotificationsBell";

interface SidebarProps {
  className?: string;
}

const ROLE_LABELS: Record<string, string> = {
  COMMUNITY_ADMIN: "Administrador del barrio",
  RESIDENT: "Residente",
  SECURITY: "Seguridad",
  MAINTENANCE: "Mantenimiento",
  AMENITY_STAFF: "Personal de amenities",
};

export function Sidebar({ className }: SidebarProps) {
  const navigate = useNavigate();
  const { data: user, isLoading } = trpc.user.me.useQuery();
  const { selectedCommunityId, setSelectedCommunityId } = useCommunity();

  const { data: communities } = trpc.amenity.getUserCommunities.useQuery();

  // Auto-select first community if none selected
  const effectiveCommunityId =
    selectedCommunityId ||
    (communities && communities.length > 0 ? communities[0].id : null);

  const currentCommunity = communities?.find(
    (c) => c.id === effectiveCommunityId,
  );

  const isSecurityGuard = currentCommunity?.role === "SECURITY";

  const getInitials = (name?: string | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div
      className={cn("flex h-screen w-64 flex-col border-r bg-card", className)}
    >
      {/* User Info Section */}
      <div className="p-6">
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage
              src={user?.image || undefined}
              alt={user?.name || "Usuario"}
            />
            <AvatarFallback>{getInitials(user?.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 overflow-hidden">
            {isLoading ? (
              <div className="h-4 w-24 animate-pulse rounded bg-muted" />
            ) : (
              <>
                <p className="truncate font-medium">
                  {user?.name || "Usuario"}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {user?.email || ""}
                </p>
              </>
            )}
          </div>
          <NotificationsBell />
        </div>
      </div>

      <Separator />

      {/* Community Selector */}
      {communities && communities.length > 0 && (
        <>
          <div className="p-4">
            <div className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Building2 className="h-3 w-3" />
              Barrio
            </div>
            <Select
              value={effectiveCommunityId || undefined}
              onValueChange={setSelectedCommunityId}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Seleccionar barrio">
                  {currentCommunity?.name}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {communities.map((community) => (
                  <SelectItem key={community.id} value={community.id}>
                    {community.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentCommunity?.unit && (
              <p className="mt-2 text-xs text-muted-foreground">
                Unidad:{" "}
                <span className="font-medium">
                  {currentCommunity.unit.unitNumber}
                  {currentCommunity.unit.blockSection &&
                    ` (${currentCommunity.unit.blockSection})`}
                </span>
              </p>
            )}
            {user?.isSuperAdmin && currentCommunity && (
              <p className="mt-1 text-xs text-muted-foreground">
                Testeando como:{" "}
                <span className="font-medium">
                  {ROLE_LABELS[currentCommunity.role] ||
                    currentCommunity.role.replace(/_/g, " ")}
                </span>
              </p>
            )}
          </div>
          <Separator />
        </>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        <div className="mb-1 px-3 text-[11px] font-semibold tracking-wider text-muted-foreground">
          MENÚ
        </div>
        <div className="space-y-1">
          <Link
            to="/"
            className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:bg-primary [&.active]:text-primary-foreground"
          >
            <Home className="h-4 w-4" />
            Inicio
          </Link>
          {isSecurityGuard ? (
            <Link
              to="/guard"
              className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:bg-primary [&.active]:text-primary-foreground"
            >
              <ScanLine className="h-4 w-4" />
              Acceso
            </Link>
          ) : (
            <>
              <Link
                to="/amenities"
                className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:bg-primary [&.active]:text-primary-foreground"
              >
                <Calendar className="h-4 w-4" />
                Amenities
              </Link>
              <Link
                to="/invitations"
                className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:bg-primary [&.active]:text-primary-foreground"
              >
                <QrCode className="h-4 w-4" />
                Invitaciones
              </Link>
              <Link
                to="/tickets"
                className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:bg-primary [&.active]:text-primary-foreground"
              >
                <MessageSquare className="h-4 w-4" />
                Tickets
              </Link>
              {currentCommunity?.role === "COMMUNITY_ADMIN" && (
                <Link
                  to="/members"
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:bg-primary [&.active]:text-primary-foreground"
                >
                  <Users className="h-4 w-4" />
                  Miembros
                </Link>
              )}
            </>
          )}
        </div>

        <div className="mb-1 mt-6 px-3 text-[11px] font-semibold tracking-wider text-muted-foreground">
          CUENTA
        </div>
        <div className="space-y-1">
          <Link
            to="/settings"
            className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:bg-primary [&.active]:text-primary-foreground"
          >
            <Settings className="h-4 w-4" />
            Configuración
          </Link>
        </div>
      </nav>

      {/* Admin & Devtools */}
      {(user?.isSuperAdmin || import.meta.env.DEV) && (
        <div className="p-4 space-y-1">
          {user?.isSuperAdmin && (
            <Link
              to="/admin"
              className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:bg-primary [&.active]:text-primary-foreground"
            >
              <Shield className="h-4 w-4" />
              Admin
            </Link>
          )}
          {import.meta.env.DEV && (
            <Link
              to="/devtools"
              className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground [&.active]:bg-primary [&.active]:text-primary-foreground"
            >
              <Wrench className="h-4 w-4" />
              Devtools
            </Link>
          )}
        </div>
      )}

      <Separator />

      {/* Footer */}
      <div className="p-4 space-y-2">
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={async () => {
            await authClient.signOut();
            navigate({ to: "/login" });
          }}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Cerrar sesión
        </Button>
        <p className="text-xs text-muted-foreground">Clara v0.0.1</p>
      </div>
    </div>
  );
}
